import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, CheckSquare, Trash2, Package, Receipt, Search, ArrowRightLeft, Loader2, ShoppingBag, Droplet, Lock, ShieldAlert, BarChart3, CheckCircle2, AlertCircle, Truck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './StockUpdate.module.css';
import ex from './StockUpdateExtras.module.css';
import { useStore } from '../../../context/StoreContext';
import { useAuth } from '../../../context/AuthContext';
import { SMIIFY_API } from '../../../config/api';
import { MODES } from '../../../utils/vendorApi';

const INVENTORY_API = `${SMIIFY_API}/inventory`;
const VENDOR_API = `${SMIIFY_API}/vendors`;

export const REASON_LABEL = {
    PURCHASE: 'Vendor purchase', CUSTOMER_RETURN: 'Customer return', TRANSFER_IN: 'Transfer in', ADJUSTMENT_IN: 'Audit correction (+)', OPENING_STOCK: 'Opening stock',
    POS_SALE_REVERSAL: 'Bill edited (returned)', SALE: 'Retail sale (manual)', POS_SALE: 'Sold on bill', INTERNAL_CONSUMPTION: 'Salon use',
    PROMO_SAMPLE: 'Free sample', DAMAGE_EXPIRED: 'Damaged / expired', VENDOR_RETURN: 'Return to vendor', TRANSFER_OUT: 'Transfer out',
    LOST_THEFT: 'Lost / stolen', ADJUSTMENT_OUT: 'Audit correction (-)',
};

const fmtWhen = (v) => (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '');

const StockUpdate = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { activeStore } = useStore();
    const { hasPermission } = useAuth();

    const canView = hasPermission('inventory', 'view');
    const canBulkUpdate = hasPermission('inventory', 'bulk_update');
    const canDelete = hasPermission('inventory', 'delete');
    const canAdjustStock = hasPermission('inventory', 'adjust_stock');
    // purchases from a vendor become a purchase bill (what is owed is tracked in Vendors)
    const canVendorPurchase = hasPermission('vendors', 'record_purchase');
    const canVendorPay = hasPermission('vendors', 'record_payment');

    const storeId = activeStore?.id || activeStore?._id || localStorage.getItem('smiify_active_store_id');

    const [localData, setLocalData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [message, setMessage] = useState(null); // { type: 'ok' | 'error', text }

    const [searchTerm, setSearchTerm] = useState('');
    const [activeProductId, setActiveProductId] = useState(searchParams.get('product') || null);
    const [selectedIds, setSelectedIds] = useState([]);

    const [adjustType, setAdjustType] = useState('INWARD');
    const [adjustQty, setAdjustQty] = useState('');
    const [adjustReason, setAdjustReason] = useState('PURCHASE');
    const [unitCost, setUnitCost] = useState('');
    const [note, setNote] = useState('');

    const [history, setHistory] = useState({ loading: false, rows: [] });
    const [vendors, setVendors] = useState(null);           // loaded when a purchase is recorded
    const [vendorId, setVendorId] = useState('');
    const [vendorBillNo, setVendorBillNo] = useState('');
    const [vendorPaid, setVendorPaid] = useState('LATER');   // LATER | FULL
    const [vendorPayMode, setVendorPayMode] = useState('CASH');
    const [bulkForm, setBulkForm] = useState({ category: '', usageType: '', taxRate: '', hsnCode: '' });

    const authHeaders = useCallback((json = false) => ({
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${localStorage.getItem('smiify_token')}`,
        ...(storeId ? { 'x-store-id': storeId } : {}),
    }), [storeId]);

    const flash = (type, text) => {
        setMessage({ type, text });
        if (type === 'ok') setTimeout(() => setMessage(null), 4000);
    };

    /* ----------------------------- load products ----------------------------- */
    const fetchInventory = useCallback(async () => {
        if (!canView) { setIsLoading(false); return; }
        if (!storeId || storeId === 'global_hq') { setLocalData([]); setIsLoading(false); return; }
        setIsLoading(true);
        try {
            const response = await fetch(`${INVENTORY_API}?storeId=${encodeURIComponent(storeId)}`, { headers: authHeaders() });
            const data = await response.json().catch(() => ({}));
            if (response.ok && data.success) setLocalData(data.data || []);
            else { setLocalData([]); flash('error', data.message || 'Could not load products.'); }
        } catch (error) {
            flash('error', 'Cannot reach the server. Try again in a minute.');
        } finally {
            setIsLoading(false);
        }
    }, [storeId, canView, authHeaders]);

    useEffect(() => { fetchInventory(); }, [fetchInventory]);

    /* ----------------------------- stock history of the selected product ----------------------------- */
    const loadHistory = useCallback(async (productId) => {
        if (!productId || !storeId) return;
        setHistory({ loading: true, rows: [] });
        try {
            const response = await fetch(`${INVENTORY_API}/${productId}/movements?storeId=${encodeURIComponent(storeId)}&limit=20`, { headers: authHeaders() });
            const data = await response.json().catch(() => ({}));
            setHistory({ loading: false, rows: response.ok && data.success ? data.data || [] : [] });
        } catch {
            setHistory({ loading: false, rows: [] });
        }
    }, [storeId, authHeaders]);

    useEffect(() => { if (activeProductId) loadHistory(activeProductId); }, [activeProductId, loadHistory]);

    const isPurchase = adjustType === 'INWARD' && adjustReason === 'PURCHASE';
    useEffect(() => {
        if (!isPurchase || !canVendorPurchase || vendors !== null || !storeId) return;
        fetch(VENDOR_API, { headers: authHeaders() })
            .then((r) => r.json()).then((j) => setVendors(j.success ? (j.data || []).filter((v) => v.isActive !== false) : []))
            .catch(() => setVendors([]));
    }, [isPurchase, canVendorPurchase, vendors, storeId, authHeaders]);

    const filteredData = localData.filter((item) => {
        const q = searchTerm.toLowerCase();
        const b = item.basicInfo || {};
        return [b.productName, b.sku, b.brand, b.category].some((v) => String(v || '').toLowerCase().includes(q));
    });

    const activeProduct = useMemo(() => localData.find((p) => p._id === activeProductId) || null, [localData, activeProductId]);

    const selectProduct = (id) => {
        setActiveProductId(id);
        setAdjustQty('');
        setUnitCost('');
        setNote('');
        setMessage(null);
    };

    const handleSelectAll = (e) => setSelectedIds(e.target.checked ? filteredData.map((item) => item._id) : []);
    const handleSelectRow = (e, id) => {
        e.stopPropagation();
        setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
    };

    /* ----------------------------- single movement ----------------------------- */
    const handleTerminalSubmit = async (e) => {
        e.preventDefault();
        if (!canAdjustStock || !activeProduct) return;
        const qty = parseInt(adjustQty, 10);
        if (!qty || qty <= 0) { flash('error', 'Enter a quantity above 0.'); return; }

        setIsUpdating(true);
        setMessage(null);
        if (isPurchase && vendorId) {
            try {
                const cost = unitCost !== '' ? Number(unitCost) : Number(activeProduct.pricing?.purchasePrice) || 0;
                const response = await fetch(`${VENDOR_API}/bills`, {
                    method: 'POST', headers: authHeaders(true),
                    body: JSON.stringify({
                        vendorId, billNo: vendorBillNo.trim(), note: note.trim(),
                        items: [{ productId: activeProduct._id, qty, unitCost: cost, gstRate: 0 }],
                        ...(vendorPaid === 'FULL' && canVendorPay ? { payNow: { amount: Math.round(qty * cost), mode: vendorPayMode } } : {}),
                    }),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data.success) throw new Error(data.message || 'Could not save the purchase.');
                setLocalData((list) => list.map((item) => (item._id === activeProduct._id ? {
                    ...item,
                    stock: { ...item.stock, currentQuantity: (Number(item.stock?.currentQuantity) || 0) + qty },
                    pricing: { ...item.pricing, purchasePrice: cost },
                } : item)));
                setAdjustQty(''); setUnitCost(''); setNote(''); setVendorBillNo('');
                flash('ok', `${data.message} It also shows in Expenses.`);
                loadHistory(activeProduct._id);
            } catch (err) {
                flash('error', err.message === 'Failed to fetch' ? 'Cannot reach the server.' : err.message);
            } finally {
                setIsUpdating(false);
            }
            return;
        }
        try {
            const payload = {
                storeId,
                productId: activeProduct._id,
                actionType: adjustType,
                quantity: qty,
                reason: adjustReason,
                note: note.trim(),
                ...(adjustType === 'INWARD' && adjustReason === 'PURCHASE' && unitCost !== '' ? { unitCost: Number(unitCost) } : {}),
            };
            const response = await fetch(`${INVENTORY_API}/movement`, { method: 'POST', headers: authHeaders(true), body: JSON.stringify(payload) });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) throw new Error(data.message || 'Could not save the movement.');

            setLocalData((list) => list.map((item) => (item._id === activeProduct._id ? {
                ...item,
                stock: { ...item.stock, currentQuantity: data.currentStock, status: data.status || item.stock?.status },
                pricing: payload.unitCost !== undefined ? { ...item.pricing, purchasePrice: payload.unitCost } : item.pricing,
            } : item)));
            setAdjustQty('');
            setUnitCost('');
            setNote('');
            flash('ok', data.message || 'Stock updated.');
            loadHistory(activeProduct._id);
        } catch (err) {
            flash('error', err.message === 'Failed to fetch' ? 'Cannot reach the server.' : err.message);
        } finally {
            setIsUpdating(false);
        }
    };

    /* ----------------------------- bulk update ----------------------------- */
    const handleBulkChange = (e) => setBulkForm({ ...bulkForm, [e.target.name]: e.target.value });

    const executeBulkUpdate = async (e) => {
        e.preventDefault();
        if (!canBulkUpdate) { flash('error', "You don't have permission for bulk updates."); return; }

        const updates = {};
        if (bulkForm.category) updates['basicInfo.category'] = bulkForm.category;
        if (bulkForm.usageType) updates.usageType = bulkForm.usageType;
        if (bulkForm.taxRate !== '') updates['pricing.taxRate'] = parseInt(bulkForm.taxRate, 10);
        if (bulkForm.hsnCode) updates['pricing.hsnCode'] = bulkForm.hsnCode;
        if (!Object.keys(updates).length) { flash('error', 'Fill at least one field to update.'); return; }

        setIsUpdating(true);
        try {
            const response = await fetch(`${INVENTORY_API}/bulk-update`, {
                method: 'PUT', headers: authHeaders(true), body: JSON.stringify({ storeId, productIds: selectedIds, updates }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) throw new Error(data.message || 'Bulk update failed.');
            flash('ok', data.message || 'Products updated.');
            setBulkForm({ category: '', usageType: '', taxRate: '', hsnCode: '' });
            setSelectedIds([]);
            fetchInventory();
        } catch (err) {
            flash('error', err.message);
        } finally {
            setIsUpdating(false);
        }
    };

    /* ----------------------------- deactivate ----------------------------- */
    const handleBulkDelete = async () => {
        if (!canDelete) { flash('error', "You don't have permission to deactivate products."); return; }
        if (!window.confirm(`Deactivate ${selectedIds.length} products? They will be hidden from stock and billing.`)) return;

        setIsUpdating(true);
        try {
            const response = await fetch(`${INVENTORY_API}/deactivate`, {
                method: 'PUT', headers: authHeaders(true), body: JSON.stringify({ storeId, productIds: selectedIds }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) throw new Error(data.message || 'Could not deactivate.');
            setLocalData((list) => list.filter((item) => !selectedIds.includes(item._id)));
            setSelectedIds([]);
            flash('ok', data.message || 'Products deactivated.');
        } catch (err) {
            flash('error', err.message);
        } finally {
            setIsUpdating(false);
        }
    };

    const getUsageBadge = (type) => {
        switch (type) {
            case 'RETAIL': return <span className={styles.badgeRetail}><ShoppingBag size={10} /> Retail</span>;
            case 'PROFESSIONAL': return <span className={styles.badgePro}><Droplet size={10} /> Pro</span>;
            case 'HYBRID': return <span className={styles.badgeBoth}><Package size={10} /> Hybrid</span>;
            case 'CONSUMABLE': return <span className={styles.badgeConsumable}><Trash2 size={10} /> Supply</span>;
            default: return <span className={styles.badgeBoth}>{type || 'Unknown'}</span>;
        }
    };

    if (!isLoading && !canView) {
        return (
            <div className={styles.pageWrapper}>
                <div className={styles.emptySidebarState} style={{ height: '100vh', border: 'none' }}>
                    <ShieldAlert size={48} color="var(--h-color-danger-600)" style={{ marginBottom: '16px' }} />
                    <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Access Restricted</h2>
                    <p style={{ color: 'var(--text-secondary)' }}>You do not have permission to view inventory. Please contact your Superadmin.</p>
                    <button type="button" className={styles.outlineBtn} onClick={() => navigate('/')} style={{ marginTop: '20px' }}>Go to Dashboard</button>
                </div>
            </div>
        );
    }

    const messageBox = message && (
        <div className={`${ex.msg} ${message.type === 'ok' ? ex.msgOk : ex.msgError}`} role="status">
            {message.type === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {message.text}
        </div>
    );

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.fixedHeader}>
                <div className={styles.titleRow}><h1 className={styles.pageTitle}>Inventory Management</h1></div>
                <div className={styles.toolbarRow}>
                    <div className={styles.toolbarLeft}>
                        <div className={styles.mainTabs}>
                            <button type="button" className={styles.simpleTabBtn} onClick={() => navigate('/inventory/overview')}><Package size={14} /> Stock Overview</button>
                            <button type="button" className={`${styles.simpleTabBtn} ${styles.activeTabBtn}`}><ArrowRightLeft size={14} /> Update & Movements</button>
<button type="button" className={styles.simpleTabBtn} onClick={() => navigate('/inventory/vendors')}><Truck size={14} /> Vendors</button>
                            <button type="button" className={styles.simpleTabBtn} onClick={() => navigate('/inventory/analytics')}><BarChart3 size={14} /> Analytics</button>
                        </div>
                        <div className={styles.searchBox}>
                            <Search size={14} className={styles.searchIcon} />
                            <input type="text" placeholder="Search product, brand, category or SKU..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={styles.searchInput} />
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.contentArea}>
                <div className={styles.splitLayoutWrapper}>
                    {/* ---------------- product table ---------------- */}
                    <div className={styles.leftPanel}>
                        <div className={styles.tableContainer}>
                            <div className={styles.tableScrollArea}>
                                <table className={styles.dataTable}>
                                    <thead>
                                        <tr>
                                            <th className={styles.checkboxCell}>
                                                <input type="checkbox" className={styles.compactCheckbox} checked={selectedIds.length === filteredData.length && filteredData.length > 0} onChange={handleSelectAll} aria-label="Select all" />
                                            </th>
                                            <th>Product Details</th><th>Category</th><th>Usage</th>
                                            <th className={styles.textRight}>Cost (₹)</th><th className={styles.textRight}>MRP (₹)</th><th className={styles.textRight}>GST %</th>
                                            <th>HSN Code</th><th className={styles.textRight}>Stock</th><th className={styles.textRight}>Min</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr><td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}><Loader2 className="animate-spin" size={24} style={{ margin: '0 auto', color: 'var(--h-color-primary-600)' }} /></td></tr>
                                        ) : filteredData.length === 0 ? (
                                            <tr><td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>No products found.</td></tr>
                                        ) : (
                                            filteredData.map((item) => {
                                                const isSelected = selectedIds.includes(item._id);
                                                const isActive = activeProductId === item._id && selectedIds.length === 0;
                                                const b = item.basicInfo || {}; const p = item.pricing || {}; const st = item.stock || {};
                                                const q = st.currentQuantity || 0;
                                                return (
                                                    <tr key={item._id} className={`${styles.mainRow} ${isSelected ? styles.selectedRow : ''} ${isActive ? styles.activeRow : ''}`} onClick={() => selectProduct(item._id)}>
                                                        <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                                                            <input type="checkbox" className={styles.compactCheckbox} checked={isSelected} onChange={(e) => handleSelectRow(e, item._id)} aria-label={`Select ${b.productName}`} />
                                                        </td>
                                                        <td>
                                                            <div className={styles.productCell}>
                                                                <span className={styles.brandName}>{b.brand || '-'}</span>
                                                                <div>
                                                                    <span className={styles.productName}>{b.productName || '-'}</span>
                                                                    {b.size ? <span className={styles.sizeText}>({b.size}{b.measureUnit})</span> : null}
                                                                </div>
                                                                <span className={styles.skuText}>SKU: {b.sku || '-'}</span>
                                                            </div>
                                                        </td>
                                                        <td className={styles.categoryText}>{b.category || '-'}</td>
                                                        <td>{getUsageBadge(item.usageType)}</td>
                                                        <td className={`${styles.priceCell} ${styles.textRight}`}>{p.purchasePrice || 0}</td>
                                                        <td className={`${styles.mutedCell} ${styles.textRight}`}>{p.mrp || 0}</td>
                                                        <td className={`${styles.taxCell} ${styles.textRight}`}>{p.taxRate || 0}%</td>
                                                        <td className={styles.hsnText}>{p.hsnCode || '-'}</td>
                                                        <td className={`${styles.stockLarge} ${styles.textRight} ${q === 0 ? styles.txtDanger : q <= st.lowStockAlertAt ? styles.txtWarning : styles.txtSuccess}`}>{q}</td>
                                                        <td className={`${styles.stockMinText} ${styles.textRight}`}>{st.lowStockAlertAt || 0}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ---------------- right panel ---------------- */}
                    <div className={styles.rightPanel}>
                        {selectedIds.length > 0 ? (
                            <div className={styles.bulkTerminal}>
                                <div className={styles.bulkHeader}>
                                    <CheckSquare size={20} className={styles.bulkIconLarge} />
                                    <h2 className={styles.bulkTitle}>Bulk Action Engine</h2>
                                    <p className={styles.bulkSubtitle}>{selectedIds.length} items selected</p>
                                </div>
                                <div className={styles.terminalContentScroll}>
                                    {messageBox}
                                    {!canBulkUpdate && !canDelete ? (
                                        <div className={styles.lockedStateBlock}>
                                            <Lock size={36} className={styles.lockedIcon} />
                                            <h3>Access Restricted</h3>
                                            <p>You do not have permission to update or deactivate products in bulk.</p>
                                        </div>
                                    ) : (
                                        <>
                                            <form onSubmit={executeBulkUpdate} className={styles.bulkForm}>
                                                <div className={styles.formGroup}>
                                                    <label htmlFor="bulk-category">Category</label>
                                                    <input id="bulk-category" type="text" name="category" placeholder="Leave blank to skip" value={bulkForm.category} onChange={handleBulkChange} className={styles.panelInput} disabled={isUpdating || !canBulkUpdate} />
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label htmlFor="bulk-usage">Usage Type</label>
                                                    <select id="bulk-usage" name="usageType" value={bulkForm.usageType} onChange={handleBulkChange} className={styles.panelInput} disabled={isUpdating || !canBulkUpdate}>
                                                        <option value="">-- No Change --</option>
                                                        <option value="RETAIL">Retail Only</option>
                                                        <option value="PROFESSIONAL">Professional (In-House)</option>
                                                        <option value="HYBRID">Hybrid (Both)</option>
                                                        <option value="CONSUMABLE">Consumable Supplies</option>
                                                        <option value="SAMPLE">Sample</option>
                                                    </select>
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label htmlFor="bulk-hsn">HSN Code</label>
                                                    <input id="bulk-hsn" type="text" name="hsnCode" placeholder="Override HSN" value={bulkForm.hsnCode} onChange={handleBulkChange} className={styles.panelInput} disabled={isUpdating || !canBulkUpdate} />
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label htmlFor="bulk-gst">GST Rate (%)</label>
                                                    <select id="bulk-gst" name="taxRate" value={bulkForm.taxRate} onChange={handleBulkChange} className={styles.panelInput} disabled={isUpdating || !canBulkUpdate}>
                                                        <option value="">-- No Change --</option>
                                                        <option value="0">0%</option><option value="5">5%</option><option value="12">12%</option><option value="18">18%</option><option value="28">28%</option>
                                                    </select>
                                                </div>
                                                {canBulkUpdate && (
                                                    <button type="submit" className={styles.bulkSubmitBtn} disabled={isUpdating}>
                                                        {isUpdating ? <Loader2 size={16} className="animate-spin" /> : `Update ${selectedIds.length} Items`}
                                                    </button>
                                                )}
                                            </form>
                                            {canDelete && (
                                                <>
                                                    <div className={styles.bulkDivider}></div>
                                                    <button type="button" className={styles.bulkDangerBtn} onClick={handleBulkDelete} disabled={isUpdating}><Trash2 size={14} /> Deactivate Selected</button>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        ) : activeProduct ? (
                            <div className={styles.singleTerminal}>
                                <div className={styles.terminalHeader}>
                                    <div className={styles.headerBrandLine}>
                                        <span className={styles.brandTag}>{activeProduct.basicInfo?.brand}</span>
                                        <span className={styles.skuPill}>SKU: {activeProduct.basicInfo?.sku}</span>
                                    </div>
                                    <h2 className={styles.sidebarTitle}>{activeProduct.basicInfo?.productName}</h2>
                                </div>
                                <div className={styles.terminalContentScroll}>
                                    {messageBox}
                                    <div className={styles.infoCard}>
                                        <div className={styles.cardHeader}><Receipt size={14} /> Stock & Pricing</div>
                                        <div className={styles.cardGrid}>
                                            <div className={styles.cItem}><span>In stock</span><strong>{activeProduct.stock?.currentQuantity || 0}</strong></div>
                                            <div className={styles.cItem}><span>Min level</span><strong>{activeProduct.stock?.lowStockAlertAt || 0}</strong></div>
                                            <div className={styles.cItem}><span>Cost (₹)</span><strong>{activeProduct.pricing?.purchasePrice || 0}</strong></div>
                                            <div className={styles.cItem}><span>Sell (₹)</span><strong>{activeProduct.pricing?.sellingPrice || 0}</strong></div>
                                            <div className={styles.cItem}><span>GST</span><strong>{activeProduct.pricing?.taxRate || 0}%</strong></div>
                                            <div className={styles.cItem}><span>HSN</span><strong>{activeProduct.pricing?.hsnCode || '-'}</strong></div>
                                        </div>
                                    </div>

                                    {!canAdjustStock ? (
                                        <div className={styles.lockedStateBlock}>
                                            <Lock size={36} className={styles.lockedIcon} />
                                            <h3>Access Restricted</h3>
                                            <p>You do not have permission to change stock levels.</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className={styles.sectionTitle}>Record Movement</div>
                                            <div className={styles.terminalToggles}>
                                                <button type="button" className={`${styles.toggleBtn} ${adjustType === 'INWARD' ? styles.toggleIn : ''}`} onClick={() => { setAdjustType('INWARD'); setAdjustReason('PURCHASE'); }}>
                                                    <ArrowDownToLine size={14} /> Inward
                                                </button>
                                                <button type="button" className={`${styles.toggleBtn} ${adjustType === 'OUTWARD' ? styles.toggleOut : ''}`} onClick={() => { setAdjustType('OUTWARD'); setAdjustReason('INTERNAL_CONSUMPTION'); setUnitCost(''); }}>
                                                    <ArrowUpFromLine size={14} /> Outward
                                                </button>
                                            </div>

                                            <form className={styles.terminalForm} onSubmit={handleTerminalSubmit}>
                                                <div className={styles.formGroup}>
                                                    <label htmlFor="mv-reason">Reason</label>
                                                    <select id="mv-reason" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} className={styles.panelInput} required disabled={isUpdating}>
                                                        {adjustType === 'INWARD' ? (
                                                            <>
                                                                <optgroup label="Standard inward">
                                                                    <option value="PURCHASE">Vendor purchase (new stock)</option>
                                                                    <option value="CUSTOMER_RETURN">Customer return</option>
                                                                </optgroup>
                                                                <optgroup label="Operations & transfers">
                                                                    <option value="TRANSFER_IN">Transfer in (from another branch)</option>
                                                                    <option value="ADJUSTMENT_IN">Audit correction (+)</option>
                                                                </optgroup>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <optgroup label="Standard outward">
                                                                    <option value="INTERNAL_CONSUMPTION">Salon use (in services)</option>
                                                                    <option value="SALE">Retail sale without a bill</option>
                                                                    <option value="PROMO_SAMPLE">Free sample / promotion</option>
                                                                </optgroup>
                                                                <optgroup label="Loss & transfers">
                                                                    <option value="DAMAGE_EXPIRED">Damaged or expired</option>
                                                                    <option value="VENDOR_RETURN">Return to vendor</option>
                                                                    <option value="TRANSFER_OUT">Transfer out (to another branch)</option>
                                                                    <option value="LOST_THEFT">Lost or stolen</option>
                                                                    <option value="ADJUSTMENT_OUT">Audit correction (-)</option>
                                                                </optgroup>
                                                            </>
                                                        )}
                                                    </select>
                                                    {adjustType === 'OUTWARD' && adjustReason === 'SALE' && (
                                                        <span className={ex.hint}>Products sold on a bill are taken out automatically. Use this only for sales without a bill.</span>
                                                    )}
                                                </div>

                                                {isPurchase && canVendorPurchase && (
                                                    <div className={styles.formGroup}>
                                                        <label htmlFor="mv-vendor">Vendor</label>
                                                        <select id="mv-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} className={styles.panelInput} disabled={isUpdating}>
                                                            <option value="">No vendor (stock only)</option>
                                                            {(vendors || []).map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
                                                        </select>
                                                        <span className={ex.hint}>{vendorId ? 'Saved as a purchase bill: it shows in Expenses (paid or pending) and in this vendor\'s khata.' : vendors && vendors.length === 0 ? 'No vendors yet. Add them in Inventory > Vendors to track what you owe.' : 'Choose a vendor to track the bill and what you owe.'}</span>
                                                    </div>
                                                )}
                                                {isPurchase && vendorId && (
                                                    <div className={ex.row2}>
                                                        <div className={styles.formGroup}>
                                                            <label htmlFor="mv-billno">Vendor bill no.</label>
                                                            <input id="mv-billno" type="text" maxLength={60} value={vendorBillNo} onChange={(e) => setVendorBillNo(e.target.value)} className={styles.panelInput} placeholder="Optional" disabled={isUpdating} />
                                                        </div>
                                                        <div className={styles.formGroup}>
                                                            <label htmlFor="mv-paid">Payment</label>
                                                            <select id="mv-paid" value={vendorPaid === 'FULL' ? vendorPayMode : 'LATER'} onChange={(e) => { if (e.target.value === 'LATER') setVendorPaid('LATER'); else { setVendorPaid('FULL'); setVendorPayMode(e.target.value); } }} className={styles.panelInput} disabled={isUpdating || !canVendorPay}>
                                                                <option value="LATER">Pay later (udhaar)</option>
                                                                {canVendorPay && MODES.map((m) => <option key={m.value} value={m.value}>Paid now: {m.label}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className={adjustType === 'INWARD' && adjustReason === 'PURCHASE' ? ex.row2 : undefined}>
                                                    <div className={styles.formGroup}>
                                                        <label htmlFor="mv-qty">Quantity</label>
                                                        <input id="mv-qty" type="number" min="1" step="1" placeholder="Qty" value={adjustQty} onChange={(e) => setAdjustQty(e.target.value)} className={styles.terminalInputHuge} required disabled={isUpdating} />
                                                    </div>
                                                    {adjustType === 'INWARD' && adjustReason === 'PURCHASE' && (
                                                        <div className={styles.formGroup}>
                                                            <label htmlFor="mv-cost">Cost per unit (₹)</label>
                                                            <input id="mv-cost" type="number" min="0" step="0.01" placeholder={String(activeProduct.pricing?.purchasePrice || 0)} value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className={styles.terminalInputHuge} disabled={isUpdating} />
                                                            <span className={ex.hint}>Optional. Updates the product cost.</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className={styles.formGroup}>
                                                    <label htmlFor="mv-note">Note (optional)</label>
                                                    <input id="mv-note" type="text" maxLength={300} placeholder="Vendor bill no., reason, staff name..." value={note} onChange={(e) => setNote(e.target.value)} className={styles.panelInput} disabled={isUpdating} />
                                                </div>

                                                <button type="submit" className={`${styles.terminalSubmit} ${adjustType === 'INWARD' ? styles.btnInward : styles.btnOutward}`} disabled={isUpdating}>
                                                    {isUpdating ? <Loader2 size={16} className="animate-spin" /> : isPurchase && vendorId ? 'Save purchase bill' : 'Save Movement'}
                                                </button>
                                            </form>
                                        </>
                                    )}

                                    {/* ---------------- stock history ---------------- */}
                                    <div className={ex.history}>
                                        <div className={ex.historyHead}><span>Stock history</span><small>Last 20</small></div>
                                        {history.loading ? (
                                            <div className={ex.historyEmpty}><Loader2 size={13} className="animate-spin" /> Loading...</div>
                                        ) : history.rows.length === 0 ? (
                                            <div className={ex.historyEmpty}>No movements yet.</div>
                                        ) : (
                                            <ul className={ex.historyList}>
                                                {history.rows.map((m) => (
                                                    <li key={m._id}>
                                                        <strong>{REASON_LABEL[m.reason] || m.reason}{m.invoiceNo ? ` #${m.invoiceNo}` : ''}{m.vendorName ? ` · ${m.vendorName}` : ''}{m.vendorBillNo ? ` (bill ${m.vendorBillNo})` : ''}</strong>
                                                        <span className={m.actionType === 'INWARD' ? ex.qtyIn : ex.qtyOut}>{m.actionType === 'INWARD' ? '+' : '-'}{m.quantity}</span>
                                                        <small>
                                                            {fmtWhen(m.createdAt)}, stock {m.previousStock} to {m.newStock}
                                                            {m.performedByName ? `, by ${m.performedByName}` : ''}
                                                            {m.note ? `. ${m.note}` : ''}
                                                        </small>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.emptySidebarState}>
                                <Package size={36} color="var(--h-color-primary-300)" className={styles.emptyIcon} />
                                <h4>No Product Selected</h4>
                                <p>Click a product to change its stock and see its history, or tick checkboxes for bulk changes.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockUpdate;
